import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { FileText, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * ClinicalTemplatesPanel — готови шаблони за клинични бележки.
 * (#7 — Templates като функционална секция в doctor sidebar)
 *
 * Не пише в DB — копира текста в clipboard, за да doctor-ът
 * да го пейстне в бележките или email към пациента.
 */
const TEMPLATES = [
  {
    key: "diabetes_followup",
    icon: "🩺",
    title_bg: "Диабет — последващ преглед",
    title_en: "Diabetes — followup",
    body_bg: `ДИАБЕТ — ПОСЛЕДВАЩ ПРЕГЛЕД

Субективно: Пациентът докладва [симптоми].
Обективно: HbA1c [стойност]%, Глюкоза на гладно [стойност] mg/dL.
Оценка: Контрол [добър/неоптимален].

План:
1. Метформин [доза] продължава
2. Тегло цел: -[X] kg за 3 месеца
3. Диета: 35-45g фибри/ден, omega-3 2x/седмица
4. Активност: 150 min/седмица умерена
5. Контрол HbA1c след 3 месеца

Препоръчани добавки:
- Магнезий глицинат 200-400mg
- Витамин D3 2000-4000 IU
- Berberine 500mg преди ядене (по показания)`,
    body_en: `DIABETES — FOLLOWUP

Subjective: Patient reports [symptoms].
Objective: HbA1c [value]%, Fasting glucose [value] mg/dL.
Assessment: Control [good/suboptimal].

Plan:
1. Continue metformin [dose]
2. Weight target: -[X] kg over 3 months
3. Diet: 35-45g fiber/day, omega-3 2x/week
4. Activity: 150 min/week moderate
5. Recheck HbA1c in 3 months

Recommended supplements:
- Magnesium glycinate 200-400mg
- Vitamin D3 2000-4000 IU
- Berberine 500mg pre-meal (if indicated)`,
  },
  {
    key: "hypertension_review",
    icon: "❤️",
    title_bg: "Хипертония — преглед",
    title_en: "Hypertension — review",
    body_bg: `ХИПЕРТОНИЯ — ПРЕГЛЕД

АН: [сист]/[диаст] mmHg в кабинет, [home] mmHg домашно.
Оценка: [контролирана/неконтролирана].

План:
1. [медикамент] продължава/коригира
2. DASH диета — Na <2g/ден
3. Калий: целево 3500mg/ден (банан, авокадо, картофи)
4. Аеробна активност: 30 min, 5 дни/седмица
5. Алкохол ≤1 dose/ден

Биомаркери: креатинин, K+, eGFR — [дата]
Контрол: 4 седмици`,
    body_en: `HYPERTENSION — REVIEW

BP: [sys]/[dia] mmHg office, [home] mmHg home.
Assessment: [controlled/uncontrolled].

Plan:
1. Continue/adjust [medication]
2. DASH diet — Na <2g/day
3. Potassium: target 3500mg/day (banana, avocado, potato)
4. Aerobic activity: 30 min, 5 days/week
5. Alcohol ≤1 drink/day

Biomarkers: creatinine, K+, eGFR — [date]
Followup: 4 weeks`,
  },
  {
    key: "thyroid_workup",
    icon: "🦋",
    title_bg: "Щитовидна жлеза — оценка",
    title_en: "Thyroid — workup",
    body_bg: `ЩИТОВИДНА ЖЛЕЗА — ОЦЕНКА

Симптоми: [умора/наддаване/непоносимост към студ].
Лаборатория: TSH [стойност], fT4 [стойност], fT3 [стойност], anti-TPO [стойност].
Оценка: [хипо/хипер/еутиреоидизъм, Хашимото / Грейвс].

План:
1. Левотироксин [доза] mcg/ден на празен стомах
2. Селен 200 mcg/ден (анти-TPO)
3. Йод от храна (морски риби, водорасли)
4. Избягване на сурови гойтрогени (зеле, броколи) при ниско Se
5. Контрол TSH/fT4 след 6-8 седмици`,
    body_en: `THYROID — WORKUP

Symptoms: [fatigue/weight gain/cold intolerance].
Labs: TSH [value], fT4 [value], fT3 [value], anti-TPO [value].
Assessment: [hypo/hyper/euthyroid, Hashimoto/Graves].

Plan:
1. Levothyroxine [dose] mcg/day on empty stomach
2. Selenium 200 mcg/day (if anti-TPO+)
3. Iodine from food (sea fish, seaweed)
4. Avoid raw goitrogens (cabbage, broccoli) if low Se
5. Recheck TSH/fT4 in 6-8 weeks`,
  },
  {
    key: "metabolic_reset",
    icon: "🔥",
    title_bg: "Метаболитно нулиране (12 седм.)",
    title_en: "Metabolic reset (12-wk)",
    body_bg: `МЕТАБОЛИТНО НУЛИРАНЕ — 12 СЕДМИЦИ

Цел: Намаляване на инсулинова резистентност, висцерална мазнина и възпаление.

Седмица 1-4 (Адаптация):
- 16:8 IF
- Въглехидрати <150g/ден
- 35g фибри/ден
- 8000 стъпки/ден

Седмица 5-8 (Дълбочина):
- 18:6 IF
- Въглехидрати <100g/ден
- 2x/седмица HIIT (15 min)
- Студена терапия 2 min/ден

Седмица 9-12 (Стабилизация):
- 14:10 IF (поддържане)
- Въглехидрати 150-200g/ден
- Силови 2x/седмица

Биомаркери: HOMA-IR, hsCRP, лептин — на 0/6/12 седмица`,
    body_en: `METABOLIC RESET — 12 WEEKS

Goal: Reduce insulin resistance, visceral fat and inflammation.

Week 1-4 (Adaptation):
- 16:8 IF
- Carbs <150g/day
- 35g fiber/day
- 8000 steps/day

Week 5-8 (Deepening):
- 18:6 IF
- Carbs <100g/day
- 2x/week HIIT (15 min)
- Cold therapy 2 min/day

Week 9-12 (Stabilization):
- 14:10 IF (maintenance)
- Carbs 150-200g/day
- Strength 2x/week

Biomarkers: HOMA-IR, hsCRP, leptin — at 0/6/12 weeks`,
  },
  {
    key: "lab_panel_request",
    icon: "🧪",
    title_bg: "Препоръчани изследвания",
    title_en: "Lab panel request",
    body_bg: `ПРЕПОРЪЧАНИ ИЗСЛЕДВАНИЯ

Базов метаболитен пакет:
- Глюкоза на гладно
- HbA1c
- Инсулин на гладно (за HOMA-IR)
- Липиден профил (TC, HDL, LDL, TG)
- ALT, AST, GGT
- Креатинин, eGFR
- Електролити (Na, K, Ca, Mg)
- Витамин D 25-OH
- Феритин, желязо
- TSH, fT4
- hsCRP
- Хомоцистеин (по преценка)

Допълнителни (по показание):
- Anti-TPO, anti-TG (Хашимото съмнение)
- Кортизол сутрешен
- DHEA-S
- Тестостерон общ + свободен (мъже >40)`,
    body_en: `RECOMMENDED LAB PANEL

Basic metabolic panel:
- Fasting glucose
- HbA1c
- Fasting insulin (for HOMA-IR)
- Lipid panel (TC, HDL, LDL, TG)
- ALT, AST, GGT
- Creatinine, eGFR
- Electrolytes (Na, K, Ca, Mg)
- Vitamin D 25-OH
- Ferritin, iron
- TSH, fT4
- hsCRP
- Homocysteine (case-by-case)

Additional (as indicated):
- Anti-TPO, anti-TG (Hashimoto suspicion)
- Morning cortisol
- DHEA-S
- Total + free testosterone (men >40)`,
  },
];

export default function ClinicalTemplatesPanel() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [copiedKey, setCopiedKey] = useState(null);

  const handleCopy = async (template) => {
    const txt = bg ? template.body_bg : template.body_en;
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedKey(template.key);
      toast.success(bg ? "Шаблонът е копиран в clipboard" : "Template copied to clipboard");
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error(bg ? "Грешка при копиране" : "Copy failed");
    }
  };

  return (
    <div className="space-y-3" data-testid="clinical-templates-panel">
      <div className={`text-xs font-bold flex items-center gap-1.5 px-1
        ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        <FileText className="w-4 h-4 text-violet-500" />
        {bg ? "Клинични шаблони" : "Clinical Templates"}
        <span className={`text-[9px] uppercase tracking-wider ml-auto
          ${isDark ? "text-gray-500" : "text-gray-400"}`}>
          {TEMPLATES.length}
        </span>
      </div>
      <p className={`text-[11px] leading-relaxed px-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
        {bg ? "Натиснете шаблон, за да копирате текста и да го поставите в бележките или email."
            : "Click a template to copy text and paste into notes or email."}
      </p>
      <ul className="space-y-1.5">
        {TEMPLATES.map((t) => {
          const copied = copiedKey === t.key;
          return (
            <li key={t.key}>
              <button
                onClick={() => handleCopy(t)}
                className={`w-full text-left p-2.5 rounded-lg border transition-all flex items-start gap-2
                  ${isDark ? "bg-[hsl(222,47%,8%)] border-white/5 hover:border-violet-500/40 hover:bg-violet-500/5"
                           : "bg-white border-gray-200 hover:border-violet-300 hover:bg-violet-50/50"}`}
                data-testid={`template-${t.key}`}
              >
                <span className="text-base leading-none mt-0.5">{t.icon}</span>
                <span className="flex-1 min-w-0">
                  <span className={`block text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    {bg ? t.title_bg : t.title_en}
                  </span>
                  <span className={`block text-[10px] truncate mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {(bg ? t.body_bg : t.body_en).split("\n")[0]}
                  </span>
                </span>
                {copied
                  ? <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  : <Copy className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
